/* eslint-disable no-param-reassign */
import { DatePicker } from 'antd';
import { RangePickerBaseProps } from 'antd/lib/date-picker/generatePicker';
import moment, { Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';

type FormatDate = 'DD/MM/YYYY' | 'MM/YYYY' | 'DD/MM';
type FormatType = 'fullDate' | 'monthYear' | 'dayMonth';

type Props = Omit<RangePickerBaseProps<Moment>, 'picker' | 'format'> & {
  format?: FormatDate;
};

type RangeDate = [Moment | null, Moment | null];
type RangePickerDate = RangeDate | null;

const formatDefault: FormatDate = 'DD/MM/YYYY';

const formatTypes: Record<FormatDate, FormatType> = {
  'DD/MM/YYYY': 'fullDate',
  'MM/YYYY': 'monthYear',
  'DD/MM': 'dayMonth',
};

const RangeDatePicker: React.FC<Props> = ({
  format = formatDefault,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState<RangePickerDate>(null);

  useEffect(() => {
    const inputs = containerRef.current?.querySelectorAll('input');
    if (!inputs || inputs.length === 0) return;

    const cleanups: Array<() => void> = [];

    inputs.forEach((input, index) => {
      input.maxLength = format.length;

      const onInput = () => {
        const numbers = input.value.match(/\d/g)?.join('') ?? '';

        let formatted = numbers;

        if (formatTypes[format] === 'fullDate') {
          if (numbers.length > 2 && numbers.length <= 4) {
            formatted = `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
          } else if (numbers.length > 4) {
            formatted = `${numbers.slice(0, 2)}/${numbers.slice(
              2,
              4,
            )}/${numbers.slice(4, 8)}`;
          }
        }

        if (formatTypes[format] === 'monthYear') {
          if (numbers.length > 2) {
            formatted = `${numbers.slice(0, 2)}/${numbers.slice(2, 6)}`;
          }
        }

        if (formatTypes[format] === 'dayMonth') {
          if (numbers.length > 2) {
            formatted = `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}`;
          }
        }

        input.value = formatted;
      };

      const onKeyup = () => {
        if (input.value.length === format.length) {
          const date = moment(input.value, format, true);

          if (!date.isValid()) return;

          let nextValue = value;

          if (!nextValue) {
            nextValue = !index ? [date, null] : [null, date];
          } else {
            nextValue = [...nextValue] as RangeDate;
            nextValue[index] = date;
          }

          nextValue = handleSetValue(nextValue);

          if (nextValue && nextValue[0]?.isValid() && nextValue[1]?.isValid()) {
            handleChange(nextValue, [format, format]);
          }
        }
      };

      input.addEventListener('input', onInput);
      input.addEventListener('keyup', onKeyup);

      cleanups.push(() => {
        input.removeEventListener('input', onInput);
        input.removeEventListener('keyup', onKeyup);
      });
    });

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [format, value]);

  useEffect(() => {
    setValue(props.value ?? null);
  }, [props.value]);

  function handleSetValue(nextValue: RangePickerDate) {
    let newNextValue = nextValue;

    setValue((prevValue) => {
      if (!prevValue) return nextValue;

      newNextValue = prevValue.map((prevDate, prevIndex) =>
        nextValue && nextValue[prevIndex] ? nextValue[prevIndex] : prevDate,
      ) as RangeDate;

      return newNextValue;
    });

    return newNextValue;
  }

  function handleChange(date: RangePickerDate, dateString: [string, string]) {
    handleSetValue(date);

    if (props.onChange) {
      props.onChange(date, dateString);
    }
  }

  return (
    <div ref={containerRef}>
      <DatePicker.RangePicker
        {...props}
        format={format}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};

export default RangeDatePicker;
